import React, {useEffect, useState} from 'react';
import {NavBar, Toast} from 'antd-mobile';
import {getNavBarContainerStyle} from "../../../utils/deviceUtils";
import {ArrowLeftOutlined} from '@ant-design/icons';
import {useNavigate, useLocation} from 'react-router-dom';
import {PickupInfoPanelProps} from "../../../components/PickupInfoPanel";
import {TaskPath} from "../../../enums/DeliveryNavigatePathEnum";
import {playScanFailedSound, playScanSound} from "../../../utils/scanUtils";
import {BarCodeScanner, useBarCodeScanner} from "../../../components/BarCodeScanner";
import {isTrackingNumber} from "../../../utils/BarCodeUtil";
import {parseException, showErrorNoTitle, showRepeatedScanError, parseExceptionMessageKey} from "../../../utils/exceptionUtils";
import {DeliveryTaskOrderType} from 'pages/delivery/graphql/types/DeliveryTaskOrderType';
import {useDeliveryTaskOrderList, useDeliveryTaskOrderOperations} from '../store/deliveryStore';
import {useLocationStore} from "../../../store/locationStore";
import {t} from "../../../i18n";
import {CheckCircleFill} from "antd-mobile-icons";
import {formatStopNumber} from "../../../utils/stopNumberUtils";
import customTopToast from '../../../components/CustomTopToast';
import {useLazyQuery} from "@apollo/client";
import {
    GET_DELIVERY_TASK_ORDER_BY_TRACKING_NUMBER,
    GetDeliveryTaskOrderByTrackingNumberResponse
} from "../graphql/queries/getDeliveryTaskOrderByTrackingNumber";


const PageBody: React.FC = () => {
    const location = useLocation();
    const [lastScannedItem, setLastScannedItem] = useState<PickupInfoPanelProps | null>(null);
    const [orderTotal,setOrderTotal] = useState<number>(0);
    const [trackingNumbers,setTrackingNumbers] = useState<string[]>([]);
    const {scanResult, setIsVisible} = useBarCodeScanner();
    const { position } = useLocationStore();
    const {handleTransferInScan} = useDeliveryTaskOrderOperations();
    const [transferInsData,setTransferInsData] = useState<DeliveryTaskOrderType[]>([]);
    const {deliveryTaskOrderList: deliveringTasks} = useDeliveryTaskOrderList();
    // 从 location.state 获取初始标签，默认为 'scanned'
    const initialTab = (location.state as { activeTab?: 'scanned' | 'unscanned' })?.activeTab || 'scanned';
    const [activeTab, setActiveTab] = useState<'scanned' | 'unscanned'>(initialTab);

    // 使用 useLazyQuery 来处理按需查询
    const [getDeliveryTaskOrderByTrackingNumber, { data: trackingData, loading: trackingLoading }] = useLazyQuery<GetDeliveryTaskOrderByTrackingNumberResponse>(
        GET_DELIVERY_TASK_ORDER_BY_TRACKING_NUMBER
    );
    console.log("transferInsData",transferInsData)

    useEffect(() => {
        setIsVisible(true);
        return () => {
            setIsVisible(false);
        }
    }, []);

    // 当 location.state 变化时，更新 activeTab
    useEffect(() => {
        const stateTab = (location.state as { activeTab?: 'scanned' | 'unscanned' })?.activeTab;
        if (stateTab) {
            setActiveTab(stateTab);
        }
    }, [location.state]);

    useEffect(() => {
        if (!deliveringTasks.length) return;
        
        const transferIns = deliveringTasks.filter(item => {
            const order = item?.orderWithAddress?.order;
            
            return item?.isTransferIn
                && !item.isPickedUp
                && !order?.isAbnormal
                && !order?.isFinished;
        })
                .sort((a, b) => {
                    if (a.routeName !== b.routeName) {
                        return a.routeName.localeCompare(b.routeName);
                    }
                    return a.stopNumber - b.stopNumber;
                }) || [];
        if (!transferIns.length) return;
        setTransferInsData(transferIns);
        setOrderTotal(transferIns.length);
    }, [deliveringTasks]);

    useEffect(() => {
        if (scanResult){
            handleScanSuccess(scanResult);
        }
    }, [scanResult]);

    // 处理扫描成功
    const handleScanSuccess = async (id: string) => {
        if (id === lastScannedItem?.id) {
            return;
        }

        if (!isTrackingNumber(id)){
            return;
        }

        try {
            const result = await handleTransferInScan({
                trackingNumber: id,
                scanLocation: {
                    latitude: position?.latitude || 0.0,
                    longitude: position?.longitude || 0.0
                }
            });

            if (result) {
                await playScanSound();
                customTopToast.show({
                    content: (
                        <div className="flex items-center justify-center gap-2">
                            <CheckCircleFill className="text-2xl text-green-500" />
                            <span className="text-xs text-white">{t('pages.delivery.task.transferIn.scanSuccess')}</span>
                        </div>
                    ),
                    position: 'top',
                    duration: 2000
                });
                setLastScannedItem({
                    id,
                    status: 'success',
                    item: {
                        trackingNumber: result.trackingNumber,
                        stopNumber: result.stopNumber.toString(),
                        subStopNumber: result.subStopNumber,
                        routeName: result.routeName,
                        driverName: result.transferFromDriverName || '-'
                    }
                });

                setTrackingNumbers([...trackingNumbers, result.trackingNumber]);
            } else {
                await playScanFailedSound();
                showErrorNoTitle('Wrong Package');
            }
        } catch (error) {
            console.error('handleScanSuccess error:', error);
            const messageKey = parseExceptionMessageKey(error);
            if (messageKey === "errors.business.alreadyScanned") {
                const isTransferInFlag = true;
                showRepeatedScanError(error);
                // 通过trackingNumber调一个查询接口，给LastScannedItem赋值。
                getDeliveryTaskOrderByTrackingNumber({
                    variables: { trackingNumber: id , isTransferIn : isTransferInFlag}
                }).then(({ data }) => {
                    if (data?.getDeliveryTaskOrderByTrackingNumber) {
                        const result = data.getDeliveryTaskOrderByTrackingNumber;
                        setLastScannedItem({
                            id,
                            status: 'success',
                            item: {
                                trackingNumber: result.trackingNumber,
                                stopNumber: result.stopNumber.toString(),
                                subStopNumber: result.subStopNumber,
                                routeName: result.routeName,
                                driverName: result.transferFromDriverName || '-'
                            }
                        });
                    }
                });
            } else {
                showErrorNoTitle(error);
                await playScanFailedSound();
            }
        }
    };

    const scannedList = transferInsData.filter(item => trackingNumbers.includes(item.trackingNumber));
    const unscannedList = transferInsData.filter(item => !trackingNumbers.includes(item.trackingNumber));

    return (
        <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
            {/*扫描结果*/}
            {lastScannedItem && lastScannedItem.item && (
                <div className="bg-[#e6f0fd] px-4 py-2">
                        <div className="text-base text-black font-medium mb-1 text-center">
                            <span className="font-medium">{t('pages.delivery.detail.trackingNumber')}</span>: 
                            <p className="font-medium break-all">
                                {lastScannedItem.item.trackingNumber}
                            </p>
                        </div>
                    <div className="flex flex-row">
                        <div className="flex-1 flex flex-col items-center justify-center">
                            <span className="text-2xl font-bold text-green-600 leading-none">{lastScannedItem.item.driverName}</span>
                            <span className="text-xs text-green-600 mt-1">{t('pages.delivery.task.transferIn.transferFrom')}</span>
                        </div>
                        <div className="flex-1 flex flex-col items-center justify-center">
                            <span className="text-3xl font-bold text-blue-600 leading-none">{lastScannedItem.item.stopNumber}</span>
                            <span className="text-xs text-blue-600 mt-1">{t('pages.delivery.detail.stopNo')}</span>
                        </div>
                    </div>
                </div>
            )}
            <div className="flex flex-row bg-white border-b border-gray-200">
                <div
                    className={`flex-1 text-center py-2 cursor-pointer ${activeTab === 'scanned' ? 'text-blue-600 font-bold border-b-2 border-blue-600' : 'text-gray-400'}`}
                    onClick={() => setActiveTab('scanned')}
                >
                    {t('pages.scanned')}({scannedList.length})
                </div>
                <div
                    className={`flex-1 text-center py-2 cursor-pointer ${activeTab === 'unscanned' ? 'text-blue-600 font-bold border-b-2 border-blue-600' : 'text-gray-400'}`}
                    onClick={() => setActiveTab('unscanned')}
                >
                    {t('pages.unScanned')}({unscannedList.length})
                </div>
            </div>
            {/* 列表区，滚动且不会遮挡按钮 */}
            <div className="flex-1 min-h-0 overflow-y-auto bg-white">
                {(activeTab === 'scanned' ? scannedList : unscannedList).map((item, idx) => {
                    const order = item.orderWithAddress?.order;
                    const isRedelivery = order?.deliveryAttempts > 0 || order?.nextAction === 'ReDeliver';
                    
                    return (
                        <div key={item.trackingNumber + idx} 
                             className={`flex flex-col px-4 py-2 border-b ${isRedelivery ? 'bg-[#fffbe6]' : ''}`}>
                            <div className="grid grid-cols-12 mb-1 font-medium">
                                <div className="col-span-6 text-sm px-1 break-all">{item.trackingNumber}</div>
                                <div className="col-span-6 text-sm px-1 break-all">{t('pages.delivery.task.transferIn.transferFrom')}：{item.transferFromDriverName || '-'}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const TransferInScanPage: React.FC = () => {
    const navigate = useNavigate();

    return (
        <div className="flex flex-col h-screen bg-white overflow-hidden">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                    onBack={() => navigate(TaskPath.DELIVERY_TASK)}
                    backIcon={<ArrowLeftOutlined/>}
                    className="font-bold text-lg justify-center"
                >
                    {t('pages.delivery.task.transferInTitle')}
                </NavBar>
            </div>
            <BarCodeScanner />
            <PageBody />
        </div>
    );
};

export default TransferInScanPage;